import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../Dashboard/components/DashboardWrapper'
import InnerBanner from '../../components/InnerBanner'
import { PRIVATE_ROUTES } from '../../Routes/routes'
import { useGetProfileUserInfo } from '../../hooks/useGetProfileUserInfo'
import {
  getCompanyContracts,
  signCompanyContract,
} from '../../services/NewServicesCompnay'
import Toast from '../../components/Toast/Toast'
import { normalizeCompanyContactSignature } from '../../utils/companyOnboarding'
import dayjs from 'dayjs'
import {
  ContractDocumentNav,
  ContractDocumentViewer,
  DEFAULT_VIEWER_HEIGHT,
} from '../../components/ContractDocumentViewer/ContractDocumentViewer'

// ─── Contract status badge ────────────────────────────────────────────────────
const StatusBadge = ({ signed }) => (
  <span
    className="rounded-pill px-3 py-1"
    style={
      signed
        ? { background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 700, border: '1px solid #bbf7d0' }
        : { background: '#fef9c3', color: '#92400e', fontSize: 12, fontWeight: 700, border: '1px solid #fde68a' }
    }
  >
    {signed ? 'Firmado' : 'Pendiente de firma'}
  </span>
)

// ─── Signature panel ──────────────────────────────────────────────────────────
const SignaturePanel = ({
  contractEntryId,
  companyName,
  existingSignature,
  isAlreadySigned,
  finalPdfUrl,
  initialPdfUrl,
  onSigned,
  onSignedSuccess,
}) => {
  const [signName, setSignName] = useState(existingSignature)
  const [accepted, setAccepted] = useState(isAlreadySigned)
  const [signed, setSigned] = useState(isAlreadySigned)

  useEffect(() => {
    setSignName(existingSignature)
    setAccepted(isAlreadySigned)
    setSigned(isAlreadySigned)
  }, [existingSignature, isAlreadySigned, contractEntryId])

  const canSign = signName.trim().length >= 3 && accepted

  const today = new Date().toLocaleDateString('es-ES', {
    day: '2-digit', month: 'long', year: 'numeric',
  })

  const { mutate: signContractMutate, isLoading: isSigning } = useMutation(
    signCompanyContract,
    {
      onSuccess: ({ response, res }) => {
        if (!response?.ok) {
          Toast({ icon: 'error', title: res?.msg || res?.error || 'No se pudo guardar la firma' })
          return
        }
        setSigned(true)
        Toast({ title: 'Firma guardada correctamente', timer: 1800 })
        onSignedSuccess?.()
      },
      onError: () => {
        Toast({ icon: 'error', title: 'No se pudo guardar la firma' })
      },
    }
  )

  const handleSign = () => {
    if (!canSign) return
    if (!contractEntryId) {
      Toast({ icon: 'error', title: 'No se encontró un contrato para firmar' })
      return
    }
    signContractMutate({
      signature: signName.trim(),
      accept: true,
      constactId: Number(contractEntryId),
    })
  }

  if (signed) {
    const downloadUrl = finalPdfUrl || initialPdfUrl
    return (
      <div className="d-flex flex-column align-items-center gap-3 text-center py-2">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 60, height: 60, background: '#dcfce7', border: '2px solid #86efac' }}
        >
          <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 4 }}>¡Contrato firmado!</p>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 0 }}>Recibirás una copia por email.</p>
        </div>
        <div className="rounded p-3 w-100" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Firmado por</p>
          <p style={{ fontFamily: '"Dancing Script", cursive', fontSize: 28, color: '#1e40af', marginBottom: 2 }}>{signName}</p>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 0 }}>{today}</p>
        </div>

        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: 13, fontWeight: 600, borderRadius: 8, padding: 10, textDecoration: 'none' }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Descargar contrato
          </a>
        )}

        <button
          type="button"
          onClick={() => onSigned && onSigned()}
          className="btn btn-bg-CTA-primary w-100"
          style={{ fontSize: 14 }}
        >
          Continuar al dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-4">
      {/* Who signs */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: 6 }}>
          ¿Quién firmará en nombre de {companyName}?
        </p>
        <div className="rounded p-3" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 32, height: 32, background: '#ede9fe', color: '#7c3aed', fontWeight: 800, fontSize: 13 }}
            >
              {companyName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="mb-0" style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{companyName}</p>
              <p className="mb-0" style={{ fontSize: 11, color: '#9ca3af' }}>Representante legal</p>
            </div>
          </div>
        </div>
      </div>

      {/* Name input */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', display: 'block', marginBottom: 8 }}>
          Nombre completo para firmar <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control text-center"
          placeholder="Escribe tu nombre completo"
          value={signName}
          onChange={(e) => setSignName(e.target.value)}
          style={{ fontSize: 15 }}
          autoComplete="off"
        />
      </div>

      {/* Live signature preview */}
      <div
        className="rounded d-flex align-items-center justify-content-center"
        style={{
          minHeight: 88,
          background: signName.trim() ? '#fafafa' : '#f9fafb',
          border: `2px dashed ${signName.trim() ? '#c7d2fe' : '#e5e7eb'}`,
          transition: 'border-color 0.2s',
        }}
      >
        {signName.trim() ? (
          <span style={{ fontFamily: '"Dancing Script", cursive', fontSize: 34, color: '#1e40af', userSelect: 'none' }}>
            {signName}
          </span>
        ) : (
          <span style={{ fontSize: 13, color: '#d1d5db', fontStyle: 'italic' }}>Tu firma aparecerá aquí</span>
        )}
      </div>

      {/* Date stamp */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 rounded" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#0284c7" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>Fecha de firma: {today}</span>
      </div>

      {/* Checkbox */}
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="acceptTerms"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          style={{ cursor: 'pointer', width: 16, height: 16, marginTop: 2 }}
        />
        <label
          className="form-check-label"
          htmlFor="acceptTerms"
          style={{ fontSize: 13, color: '#374151', cursor: 'pointer', paddingLeft: 6, lineHeight: 1.5 }}
        >
          He leído y acepto los <strong>términos y condiciones</strong> del contrato de servicios Hutrit Club.
        </label>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={handleSign}
        disabled={!canSign}
        className="btn btn-bg-CTA-primary w-100"
        style={{ fontSize: 15, fontWeight: 700, padding: 12, opacity: canSign ? 1 : 0.45, transition: 'opacity 0.2s' }}
      >
        {isSigning ? 'Guardando firma...' : 'Firmar contrato'}
      </button>

      <button
        type="button"
        onClick={() => onSigned && onSigned()}
        className="btn btn-white border border-on-bg-primary w-100"
        style={{ fontSize: 14 }}
      >
        Firmar más tarde
      </button>
    </div>
  )
}

// ─── Contract meta info bar ───────────────────────────────────────────────────
const ContractMetaBar = ({ contract }) => {
  if (!contract) return null
  const sig = contract.contract_signature ?? {}
  const status = contract?.contract_signature?.contract_signature_status?.name ?? '—'
  const startDate = contract.start_date ? dayjs(contract.start_date).format('DD/MM/YYYY') : '—'
  const endDate = contract.end_date ? dayjs(contract.end_date).format('DD/MM/YYYY') : '—'
  const signedAt = sig.signed_at ? dayjs(sig.signed_at).format('DD/MM/YYYY') : null

  return (
    <div
      className="d-flex flex-wrap gap-3 mb-3 p-3 rounded"
      style={{ background: '#f9fafb', border: '1px solid #f3f4f6', fontSize: 12 }}
    >
      <div>
        <span style={{ color: '#9ca3af', marginRight: 4 }}>Estado:</span>
        <span style={{ fontWeight: 600, color: '#111827' }}>{status}</span>
      </div>
      <div>
        <span style={{ color: '#9ca3af', marginRight: 4 }}>Vigencia:</span>
        <span style={{ fontWeight: 600, color: '#111827' }}>{startDate} — {endDate}</span>
      </div>
      <div>
        <span style={{ color: '#9ca3af', marginRight: 4 }}>Monto:</span>
        <span style={{ fontWeight: 600, color: '#111827' }}>
          ${contract.company_rate ?? '—'}
          {contract.company_tax_applies
            ? ` + (IVA ${contract.company_tax_percent ?? '—'}%)`
            : ''}
        </span>
      </div>
      {signedAt && (
        <div>
          <span style={{ color: '#9ca3af', marginRight: 4 }}>Firmado:</span>
          <span style={{ fontWeight: 600, color: '#166534' }}>{signedAt}</span>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
const ContractSigning = ({
  embedded = false,
  onContractSigned,
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: userData = {} } = useGetProfileUserInfo()

  const { data: contractsData, isLoading: isLoadingContracts } = useQuery({
    queryKey: ['company-contracts', 0],
    queryFn: () => getCompanyContracts(0),
  })

  const contracts = contractsData?.res?.list ?? []

  const [currentIndex, setCurrentIndex] = useState(0)
  // Track contracts signed in this session by their ID
  const [localSignedIds, setLocalSignedIds] = useState(new Set())

  const currentContract = contracts[currentIndex] ?? null
  const sig = currentContract?.contract_signature ?? {}

  const existingSignature = normalizeCompanyContactSignature(sig.references?.companyContactSignature)
  const isAlreadySigned = Boolean(existingSignature) || localSignedIds.has(currentContract?.id)

  const companyName =
    userData?.userInfo?.company_name ||
    userData?.userInfo?.name ||
    sig.references?.companyName ||
    'Tu empresa'

  const handleDone = () => {
    if (embedded && onContractSigned) {
      onContractSigned()
      return
    }
    navigate(PRIVATE_ROUTES.dashboard)
  }

  const handleSignedSuccess = () => {
    setLocalSignedIds((prev) => new Set(prev).add(currentContract?.id))
    queryClient.invalidateQueries(['company-contracts', 0])
    if (embedded && onContractSigned) {
      onContractSigned()
    }
  }

  const handleIndexChange = (next) => {
    if (next < 0 || next >= contracts.length) return
    setCurrentIndex(next)
  }

  const loadingContent = (
    <div className="d-flex justify-content-center align-items-center py-5">
      <div className="spinner-border spinner-border-sm text-secondary" role="status" />
    </div>
  )

  if (isLoadingContracts) {
    return embedded ? loadingContent : (
      <DashboardWrapper dashboardWrapperClassName="w-100">{loadingContent}</DashboardWrapper>
    )
  }

  if (!contracts.length) {
    const emptyContent = (
      <>
        {!embedded && (
          <InnerBanner
            before="Perfil de empresa"
            beforeRoute={PRIVATE_ROUTES.company_form}
            text="Revisar y firmar el contrato"
            className="mb-3"
          />
        )}
        <p style={{ fontSize: 14, color: '#6b7280' }}>No hay contratos disponibles en este momento.</p>
        <button type="button" className="btn btn-bg-CTA-primary" onClick={handleDone}>
          {embedded ? 'Continuar' : 'Ir al dashboard'}
        </button>
      </>
    )
    return embedded ? emptyContent : (
      <DashboardWrapper dashboardWrapperClassName="w-100">
        <div className="p-3 p-lg-4" style={{ maxWidth: 700, margin: '0 auto' }}>{emptyContent}</div>
      </DashboardWrapper>
    )
  }

  const signingContent = (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');`}</style>

      <div className={embedded ? '' : 'p-3 p-lg-4'} style={embedded ? undefined : { maxWidth: 1300, margin: '0 auto' }}>

          {!embedded && (
            <InnerBanner
              before="Perfil de empresa"
              beforeRoute={PRIVATE_ROUTES.company_form}
              text="Revisar y firmar el contrato"
              className="mb-3"
            />
          )}

          {/* Page heading */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <h2 className="text-bg-brand mb-0" style={{ fontWeight: 800, fontSize: 22 }}>
              {isAlreadySigned ? 'Contrato firmado' : 'Revisar y firmar el contrato'}
            </h2>
            <StatusBadge signed={isAlreadySigned} />
          </div>
          <p className="mb-4" style={{ fontSize: 14, color: '#6b7280' }}>
            {isAlreadySigned
              ? embedded
                ? 'Este contrato ya fue firmado. Puedes descargar una copia o ir al paso de Mi contrato.'
                : 'Este contrato ya fue firmado. Puedes descargar una copia o continuar al dashboard.'
              : 'Revisa todos los detalles y firma el contrato para activar tu cuenta.'}
          </p>

          {/* Navigation between contracts */}
          <ContractDocumentNav
            total={contracts.length}
            current={currentIndex}
            onChange={handleIndexChange}
          />

          {/* Contract metadata */}
          <ContractMetaBar contract={currentContract} />

          {/* Main two-column layout — viewer always visible */}
          <div className="row g-4 align-items-start">

            <div className="col-12 col-lg-7">
              <ContractDocumentViewer
                finalPdfUrl={sig.final_pdf_url}
                initialDocxUrl={sig.initial_pdf_url}
                height={DEFAULT_VIEWER_HEIGHT}
              />
            </div>

            <div className="col-12 col-lg-5">
              <div
                className="bg-white rounded border p-4"
                style={{
                  borderColor: '#e5e7eb',
                  position: isAlreadySigned ? 'static' : 'sticky',
                  top: isAlreadySigned ? undefined : 24,
                }}
              >
                <p
                  className="mb-3 pb-3"
                  style={{ fontWeight: 700, fontSize: 15, color: '#111827', borderBottom: '1px solid #f3f4f6' }}
                >
                  {isAlreadySigned ? 'Estado de la firma' : 'Bloque de firma digital'}
                </p>
                <SignaturePanel
                  key={currentContract?.id}
                  contractEntryId={currentContract?.id}
                  companyName={companyName}
                  existingSignature={existingSignature}
                  isAlreadySigned={isAlreadySigned}
                  finalPdfUrl={sig.final_pdf_url}
                  initialPdfUrl={sig.initial_pdf_url}
                  onSigned={handleDone}
                  onSignedSuccess={handleSignedSuccess}
                />
              </div>
            </div>

          </div>

        </div>
    </>
  )

  if (embedded) return signingContent

  return (
    <DashboardWrapper dashboardWrapperClassName="w-100">
      {signingContent}
    </DashboardWrapper>
  )
}

export default ContractSigning
